import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DatePickerModule } from 'primeng/datepicker';
import { firstValueFrom } from 'rxjs';
import { AuthorizedApiService } from '@core';
import { AuthorizationService } from '@core/service/authorization.service';
import { CompanySelectionService } from '@core/service/company-selection.service';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import { ConfirmDeleteDialogComponent } from '@shared/components/confirm-delete-dialog/confirm-delete-dialog.component';
import { LoadingStateComponent } from '@shared/components/loading-state/loading-state.component';
import { OrgUnitTreeSelectComponent } from '@shared/components/org-unit-tree-select/org-unit-tree-select.component';
import { CompanySelectComponent } from '@shared/components/company-select/company-select.component';
import { extractApiErrorMessage } from '@shared/utils/api-error.util';
import { CompaniesRecord } from '../../organization/companies-records/companies-record.interface';
import { EmployeeRecord } from '../../organization/employees/employee.interface';
import { AbsenceJustificationDialogComponent } from '../absence/absence-justification-dialog.component';
import { clearAttendanceWeekFilterState, persistAttendanceWeekFilterState, restoreAttendanceWeekFilterState } from '../attendance-week-filter-state.util';
import { AttendanceResultGroupedResponse, AttendanceResultRecalculatePayload, AttendanceResultRecord } from './attendance-result.interface';

interface OrgUnitRecord { orgUnitId: string; companiesRecordId: string; name: string; parentId?: string | null; type?: string | null; orgPath?: string | null; isActive?: boolean; }
interface CollectionResponse<T> { data?: T[]; items?: T[]; results?: T[]; companiesRecords?: T[]; orgUnits?: T[]; employees?: T[]; }
interface ResultDay { date: Date; value: string; weekday: string; dayLabel: string; isToday: boolean; }
interface ResultRow { employeeId: string; employeeNumber: string; employeeName: string; orgUnitName: string; cells: Map<string, AttendanceResultRecord>; }
type JustificationFlow = 'absence' | 'delay' | 'early_exit';
interface JustificationOption { flow: JustificationFlow; label: string; icon: string; minutes?: number; }
type ResultViewFilter = 'all' | 'incidents' | 'delay' | 'incomplete' | 'absent' | 'complete';

@Component({
  selector: 'app-attendance-result',
  standalone: true,
  imports: [CommonModule, FormsModule, BreadcrumbComponent, LoadingStateComponent, OrgUnitTreeSelectComponent, CompanySelectComponent,
    MatAutocompleteModule, MatButtonModule, DatePickerModule, MatFormFieldModule, MatIconModule, MatInputModule, MatMenuModule,
    MatPaginatorModule, MatSelectModule, TranslateModule],
  templateUrl: './attendance-result.component.html',
  styleUrl: './attendance-result.component.scss',
})
export class AttendanceResultComponent implements OnInit {
  private static readonly FILTER_STATE_KEY = 'attendance.results.weekly-filters.v1';
  companies: CompaniesRecord[] = [];
  orgUnits: OrgUnitRecord[] = [];
  employees: EmployeeRecord[] = [];
  results: AttendanceResultRecord[] = [];
  rows: ResultRow[] = [];
  weekDays: ResultDay[] = [];
  isLoading = true;
  currentPage = 1;
  pageSize = 50;
  totalItems = 0;
  employeeSearchText: string | EmployeeRecord = '';
  selectedQuickResult: AttendanceResultRecord | null = null;
  selectedSummaryRow: ResultRow | null = null;
  activeViewFilter: ResultViewFilter = 'all';
  filters = this.defaultFilters();
  rangeStartDate: Date | null = null;
  rangeEndDate: Date | null = null;
  rangePickerValue: Date[] | null = null;
  readonly statusOptions = ['pending', 'present', 'absent', 'incomplete', 'justified', 'corrected'] as const;

  private readonly apiService = inject(AuthorizedApiService);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly companySelection = inject(CompanySelectionService);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private employeeSearchHandle: ReturnType<typeof setTimeout> | null = null;
  private requestId = 0;

  ngOnInit(): void {
    this.restoreFilterState();
    this.syncRangeDates();
    this.buildWeek();
    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => { this.buildWeek(); this.buildRows(); });
    void this.initialize();
  }

  get selectedEmployee(): EmployeeRecord | null { return this.employees.find((item) => item.employeeId === this.filters.employeeId) || null; }
  get selectedDateRangeLabel(): string {
    const start = this.rangeStartDate; const end = this.rangeEndDate;
    if (!start || !end) return '—';
    const part = (value: number): string => `${value}`.padStart(2, '0');
    const startDay = part(start.getDate()); const endDay = part(end.getDate());
    const startMonth = part(start.getMonth() + 1); const endMonth = part(end.getMonth() + 1);
    if (start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth()) {
      return `${startDay}–${endDay}/${endMonth}/${end.getFullYear()}`;
    }
    if (start.getFullYear() === end.getFullYear()) {
      return `${startDay}/${startMonth}–${endDay}/${endMonth}/${end.getFullYear()}`;
    }
    return `${startDay}/${startMonth}/${start.getFullYear()}–${endDay}/${endMonth}/${end.getFullYear()}`;
  }
  get canCreateJustification(): boolean { return this.authorizationService.hasPermission('attendanceJustifications', 'create'); }
  get canRecalculate(): boolean { return this.authorizationService.hasAccess({ resource: 'attendanceResults', action: 'update' }); }
  readonly displayEmployee = (value: string | EmployeeRecord | null): string => !value ? '' : typeof value === 'string' ? value : this.employeeLabel(value);

  get displayedRows(): ResultRow[] {
    if (this.activeViewFilter === 'all') return this.rows;
    if (this.activeViewFilter === 'incidents') {
      return this.rows.filter((row) => [...row.cells.values()].some((result) => this.hasResultIncident(result)));
    }
    return this.rows.filter((row) => this.rowCategory(row) === this.activeViewFilter);
  }

  get summaryTotal(): number { return this.rows.length; }
  get summaryComplete(): number { return this.countRows('complete'); }
  get summaryDelay(): number { return this.countRows('delay'); }
  get summaryIncomplete(): number { return this.countRows('incomplete'); }
  get summaryAbsent(): number { return this.countRows('absent'); }
  get summaryIncidents(): number { return this.rows.filter((row) => [...row.cells.values()].some((result) => this.hasResultIncident(result))).length; }
  get selectedSummaryResults(): AttendanceResultRecord[] {
    return this.selectedSummaryRow
      ? [...this.selectedSummaryRow.cells.values()].sort((left, right) => left.workDate.localeCompare(right.workDate))
      : [];
  }
  get visibleSummaryRow(): ResultRow | null { return this.selectedQuickResult ? null : this.selectedSummaryRow; }
  get weekRangeLongLabel(): string {
    if (!this.rangeStartDate || !this.rangeEndDate) return this.selectedDateRangeLabel;
    const locale = this.translate.currentLang?.startsWith('en') ? 'en-US' : 'es-MX';
    const start = new Intl.DateTimeFormat(locale, { day: '2-digit', month: 'long' }).format(this.rangeStartDate);
    const end = new Intl.DateTimeFormat(locale, { day: '2-digit', month: 'long', year: 'numeric' }).format(this.rangeEndDate);
    return `${start} – ${end}`;
  }
  get quickJourneyName(): string { return this.quickLaborContextValue(['journeyName', 'journey.name']) || 'Jornada no disponible'; }
  get quickScheduleRange(): string {
    const start = this.quickLaborContextValue(['startTime', 'dailySchedule.startTime', 'schedule.startTime']);
    const end = this.quickLaborContextValue(['endTime', 'dailySchedule.endTime', 'schedule.endTime']);
    return start && end ? `${start} – ${end}` : `${this.selectedQuickResult?.scheduledMinutes || 0} min programados`;
  }

  async initialize(): Promise<void> {
    this.isLoading = true;
    try { await this.loadCompanies(); await Promise.all([this.loadOrgUnits(), this.loadEmployees()]); await this.loadData(); }
    finally { this.isLoading = false; }
  }

  async applyFilters(): Promise<void> { this.currentPage = 1; this.persistFilterState(); this.buildWeek(); await this.loadData(); }
  async clearFilters(): Promise<void> {
    this.filters = this.defaultFilters();
    this.filters.companiesRecordId = this.companies.length === 1
      ? this.companySelection.resolveSelection(this.companies)
      : '';
    this.companySelection.rememberSelection(this.filters.companiesRecordId);
    this.syncRangeDates();
    this.employeeSearchText = ''; this.currentPage = 1; clearAttendanceWeekFilterState(AttendanceResultComponent.FILTER_STATE_KEY); this.buildWeek();
    await Promise.all([this.loadOrgUnits(), this.loadEmployees()]); await this.loadData();
  }
  async changeWeek(offset: number): Promise<void> {
    const start = this.parseDate(this.filters.weekDate) || new Date();
    const end = this.parseDate(this.filters.dateTo) || new Date(start);
    const rangeLength = Math.max(1, Math.round((end.getTime() - start.getTime()) / 86400000) + 1);
    start.setDate(start.getDate() + offset * rangeLength); end.setDate(end.getDate() + offset * rangeLength);
    this.filters.weekDate = this.toDateValue(start); this.filters.dateTo = this.toDateValue(end);
    this.syncRangeDates(); await this.applyFilters();
  }
  onRangeStartChanged(value: Date | null): void {
    this.rangeStartDate = value;
    if (!value) return;
    this.filters.weekDate = this.toDateValue(value);
    if (this.rangeEndDate && this.rangeEndDate < value) { this.rangeEndDate = null; this.filters.dateTo = ''; }
  }
  async onRangeEndChanged(value: Date | null): Promise<void> {
    this.rangeEndDate = value;
    if (!this.rangeStartDate || !value) return;
    const days = Math.round((value.getTime() - this.rangeStartDate.getTime()) / 86400000) + 1;
    if (days > 31) {
      this.rangeEndDate = null; this.filters.dateTo = '';
      this.notify('snackbar-danger', 'attendanceResultsCatalog.weekly.rangeExceeded'); return;
    }
    this.filters.dateTo = this.toDateValue(value); await this.applyFilters();
  }

  async onRangePickerChanged(value: Date[] | null): Promise<void> {
    const start = value?.[0] || null;
    const end = value?.[1] || null;

    this.rangeEndDate = null;
    this.filters.dateTo = '';
    this.onRangeStartChanged(start);

    if (end) {
      await this.onRangeEndChanged(end);
    }

    this.syncRangePickerValue();
  }
  async onCompanyChanged(): Promise<void> {
    this.companySelection.rememberSelection(this.filters.companiesRecordId);
    this.filters.orgUnitId = ''; this.filters.employeeId = ''; this.employeeSearchText = '';
    await Promise.all([this.loadOrgUnits(), this.loadEmployees()]); await this.applyFilters();
  }
  async onOrgUnitChanged(): Promise<void> { this.filters.employeeId = ''; this.employeeSearchText = ''; await this.loadEmployees(); await this.applyFilters(); }
  onEmployeeInput(value: string | EmployeeRecord): void {
    this.employeeSearchText = value; const text = this.displayEmployee(value);
    if (this.selectedEmployee && text !== this.employeeLabel(this.selectedEmployee)) { this.filters.employeeId = ''; void this.applyFilters(); }
    if (this.employeeSearchHandle) clearTimeout(this.employeeSearchHandle);
    this.employeeSearchHandle = setTimeout(() => void this.loadEmployees(text), 250);
  }
  onEmployeeSelected(event: MatAutocompleteSelectedEvent): void {
    const employee = event.option.value as EmployeeRecord; this.filters.employeeId = employee.employeeId;
    this.employeeSearchText = employee; void this.applyFilters();
  }
  onPageChange(event: PageEvent): void { this.currentPage = event.pageIndex + 1; this.pageSize = event.pageSize; this.persistFilterState(); void this.loadData(); }

  async onStatusChanged(): Promise<void> { this.activeViewFilter = 'all'; await this.applyFilters(); }
  selectViewFilter(filter: ResultViewFilter): void { this.activeViewFilter = filter; }
  openQuickDetail(result: AttendanceResultRecord, preserveSummary = false): void {
    if (!preserveSummary) this.selectedSummaryRow = null;
    this.selectedQuickResult = result;
  }
  backToRowSummary(): void { this.selectedQuickResult = null; }
  closeQuickDetail(): void { this.selectedQuickResult = null; this.selectedSummaryRow = null; }
  openRowSummary(row: ResultRow): void {
    this.selectedQuickResult = null;
    this.selectedSummaryRow = row;
  }
  closeRowSummary(): void { this.selectedSummaryRow = null; }
  summaryPercentage(value: number): string { return this.summaryTotal ? `${((value / this.summaryTotal) * 100).toFixed(1)}%` : '0%'; }
  quickDate(result: AttendanceResultRecord): string { return this.formatDateLong(result.workDate); }
  quickTime(value?: string | null, timezone?: string): string { return this.formatTime(value, timezone); }
  exportResults(): void {
    const header = ['Empleado', 'Número', 'Fecha', 'Estatus', 'Entrada', 'Salida', 'Tiempo trabajado'];
    const rows = this.results.map((result) => [
      result.employeeName || result.employeeNumber,
      result.employeeNumber,
      result.workDate,
      this.statusLabel(result),
      this.quickTime(result.firstEventAt, result.timezone),
      this.quickTime(result.lastEventAt, result.timezone),
      this.workedTime(result),
    ]);
    const csv = [header, ...rows].map((row) => row.map((value) => `"${String(value ?? '').replace(/"/g, '""')}"`).join(',')).join('\r\n');
    const url = URL.createObjectURL(new Blob(['\ufeff', csv], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url; link.download = `resultados-asistencia-${this.filters.weekDate}-${this.filters.dateTo}.csv`; link.click();
    URL.revokeObjectURL(url);
  }

  private restoreFilterState(): void {
    const state = restoreAttendanceWeekFilterState(AttendanceResultComponent.FILTER_STATE_KEY);
    const restoredStart = state.weekDate || this.filters.weekDate;
    const fallbackEnd = this.parseDate(restoredStart) || new Date(); fallbackEnd.setDate(fallbackEnd.getDate() + 6);
    this.filters = {
      companiesRecordId: state.companiesRecordId || '', orgUnitId: state.orgUnitId || '',
      employeeId: state.employeeId || '', status: state.status || '', weekDate: restoredStart,
      dateTo: state.dateTo || this.toDateValue(fallbackEnd),
    };
    this.employeeSearchText = state.employeeSearchText || '';
    this.currentPage = state.currentPage || 1;
    this.pageSize = state.pageSize || this.pageSize;
  }

  private persistFilterState(): void {
    persistAttendanceWeekFilterState(AttendanceResultComponent.FILTER_STATE_KEY, {
      ...this.filters, employeeSearchText: this.displayEmployee(this.employeeSearchText),
      currentPage: this.currentPage, pageSize: this.pageSize,
    });
  }
  async viewResult(result: AttendanceResultRecord): Promise<void> { await this.router.navigate(['/attendance/attendance-results', result.attendanceResultId]); }

  async recalculate(result: AttendanceResultRecord): Promise<void> {
    if (!this.canRecalculate) return;
    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, { width: '440px', data: {
      title: this.t('attendanceResultsCatalog.dialog.recalculateTitle'),
      message: this.t('attendanceResultsCatalog.dialog.recalculateSingleMessage', { employee: result.employeeName || result.employeeNumber, workDate: result.workDate }),
      confirmLabel: this.t('attendanceResultsCatalog.dialog.recalculateConfirm'), cancelLabel: this.t('common.cancel') } });
    if (!await firstValueFrom(dialogRef.afterClosed())) return;
    this.isLoading = true;
    try {
      const payload: AttendanceResultRecalculatePayload = { employeeId: result.employeeId, workDate: result.workDate, reason: 'manual_recalculation' };
      await firstValueFrom(this.apiService.post('attendance-results/recalculate', payload));
      this.notify('snackbar-success', 'attendanceResultsCatalog.notifications.recalculationQueued'); await this.loadData();
    } catch (error) {
      this.snackBar.open(extractApiErrorMessage(error, this.t('attendanceResultsCatalog.notifications.recalculationError')), undefined,
        { duration: 4000, panelClass: ['snackbar-danger'], horizontalPosition: 'center', verticalPosition: 'bottom' });
    } finally { this.isLoading = false; }
  }

  justificationOptions(result: AttendanceResultRecord): JustificationOption[] {
    if (!this.canCreateJustification || this.hasJustification(result)) return [];
    const options: JustificationOption[] = [];
    if (result.status === 'absent') options.push({ flow: 'absence', label: this.t('attendanceResultsCatalog.weekly.justifyAbsence'), icon: 'event_available' });
    const delayMinutes = Math.max(0, Number(result.delayMinutes || 0));
    if ((result.flags?.hasDelay || delayMinutes > 0) && delayMinutes > 0) options.push({ flow: 'delay', label: this.t('attendanceResultsCatalog.weekly.justifyDelay'), icon: 'schedule', minutes: delayMinutes });
    const earlyExitMinutes = this.earlyExitMinutes(result);
    if ((result.flags?.hasEarlyExit || earlyExitMinutes > 0) && earlyExitMinutes > 0) options.push({ flow: 'early_exit', label: this.t('attendanceResultsCatalog.weekly.justifyEarlyExit'), icon: 'logout', minutes: earlyExitMinutes });
    return options;
  }

  async createJustification(result: AttendanceResultRecord, option: JustificationOption): Promise<void> {
    const dialogRef = this.dialog.open(AbsenceJustificationDialogComponent, {
      width: '720px', maxWidth: 'calc(100vw - 24px)', maxHeight: 'calc(100dvh - 24px)', autoFocus: false,
      restoreFocus: true, disableClose: true, panelClass: 'attendance-result-justification-dialog-panel',
      data: { result, ...(option.flow !== 'absence' ? { flow: option.flow } : {}), ...(option.minutes ? { minutes: option.minutes } : {}) },
    });
    const created = await firstValueFrom(dialogRef.afterClosed()); if (!created) return;
    result.hasJustification = true; result.attendanceJustificationId = created.attendanceJustificationId;
    this.buildRows(); this.notify('snackbar-success', 'attendanceJustificationsCatalog.notifications.created');
  }

  hasJustification(result: AttendanceResultRecord): boolean { return result.hasJustification === true || result.status === 'justified' || !!result.attendanceJustificationId; }
  statusIcon(result: AttendanceResultRecord): string {
    if (result.status === 'justified') return 'verified_user'; if (result.status === 'absent') return 'cancel';
    if (result.status === 'incomplete') return 'warning'; if (result.status === 'pending') return 'hourglass_top';
    if (result.status === 'corrected') return 'published_with_changes'; if (result.flags?.hasDelay) return 'schedule';
    if (result.flags?.hasEarlyExit) return 'logout'; return 'check_circle';
  }
  statusLabel(result: AttendanceResultRecord): string {
    if (result.status === 'present' && result.flags?.hasDelay) return this.t('attendanceResultsCatalog.incidentCodes.delay');
    if (result.status === 'present' && result.flags?.hasEarlyExit) return this.t('attendanceResultsCatalog.incidentCodes.early_exit');
    return this.t(`attendanceResultsCatalog.statuses.${result.status}`);
  }
  statusClass(result: AttendanceResultRecord): string {
    if (result.status === 'absent') return 'is-absent'; if (result.status === 'incomplete') return 'is-incomplete';
    if (result.status === 'pending') return 'is-pending'; if (result.status === 'justified') return 'is-justified';
    if (result.status === 'corrected') return 'is-corrected';
    if (result.flags?.hasDelay) return 'has-delay';
    if (result.flags?.hasEarlyExit) return 'has-early-exit';
    return 'is-present';
  }
  workedTime(result: AttendanceResultRecord): string { return this.formatMinutes(result.workedMinutes); }
  statusDetail(result: AttendanceResultRecord): string {
    if (result.status === 'present' && result.flags?.hasDelay) {
      const minutes = Math.max(0, Number(result.delayMinutes || 0));
      return minutes > 0 ? `+${minutes} min` : this.workedTime(result);
    }
    if (result.status === 'present' && result.flags?.hasEarlyExit) {
      const minutes = this.earlyExitMinutes(result);
      return minutes > 0 ? `-${minutes} min` : this.workedTime(result);
    }
    if (result.status === 'incomplete' &&
        (result.flags?.hasMissingPunch || result.incidentCodes?.includes('missing_punch'))) {
      return this.t('attendanceResultsCatalog.incidentCodes.missing_punch');
    }
    return this.workedTime(result);
  }
  eventTime(result: AttendanceResultRecord): string {
    const first = this.formatTime(result.firstEventAt, result.timezone); const last = this.formatTime(result.lastEventAt, result.timezone);
    return first === '—' && last === '—' ? '—' : `${first}–${last}`;
  }

  private async loadData(): Promise<void> {
    const requestId = ++this.requestId; this.isLoading = true;
    try {
      const response = await firstValueFrom(this.apiService.get<AttendanceResultGroupedResponse>('attendance-results/grouped', { params: {
        isActive: 'true', workDateFrom: this.weekDays[0]?.value || '', workDateTo: this.weekDays[6]?.value || '',
        page: this.currentPage, perPage: this.pageSize, groupBy: 'employeeId', sort: ['employeeName:asc', 'workDate:asc'],
        ...(this.filters.status ? { status: this.filters.status } : {}), ...(this.filters.employeeId ? { employeeId: this.filters.employeeId } : {}),
        ...(this.filters.companiesRecordId ? { companiesRecordId: this.filters.companiesRecordId } : {}),
        ...(this.filters.orgUnitId ? { orgUnitId: this.filters.orgUnitId } : {}), } }));
      if (requestId !== this.requestId) return;
      this.results = (response?.data || []).flatMap((group) => group.items || []).map((item) => this.normalizeResult(item));
      this.totalItems = Number(response?.pagination?.total || 0); this.buildRows();
    } catch (error) {
      if (requestId !== this.requestId) return; this.results = []; this.totalItems = 0; this.buildRows();
      this.snackBar.open(extractApiErrorMessage(error, this.t('attendanceResultsCatalog.notifications.loadError')), undefined,
        { duration: 4000, panelClass: ['snackbar-danger'], horizontalPosition: 'center', verticalPosition: 'bottom' });
    } finally { if (requestId === this.requestId) this.isLoading = false; }
  }

  private buildRows(): void {
    const rowMap = new Map<string, ResultRow>();
    this.results.forEach((result) => {
      const employee = this.employees.find((item) => item.employeeId === result.employeeId);
      const row = rowMap.get(result.employeeId) || { employeeId: result.employeeId,
        employeeNumber: result.employeeNumber || employee?.employeeNumber || '—',
        employeeName: result.employeeName || employee?.fullName || result.employeeNumber || '—',
        orgUnitName: this.orgUnits.find((item) => item.orgUnitId === result.orgUnitId)?.name || employee?.orgUnitName || '—',
        cells: new Map<string, AttendanceResultRecord>() };
      row.cells.set(result.workDate, result); rowMap.set(result.employeeId, row);
    });
    this.rows = [...rowMap.values()].sort((a, b) => a.employeeName.localeCompare(b.employeeName));
  }

  private buildWeek(): void {
    const start = this.parseDate(this.filters.weekDate) || new Date();
    const parsedEnd = this.parseDate(this.filters.dateTo); const end = parsedEnd && parsedEnd >= start ? parsedEnd : new Date(start);
    const length = Math.min(31, Math.max(1, Math.round((end.getTime() - start.getTime()) / 86400000) + 1));
    const today = this.toDateValue(new Date());
    this.weekDays = Array.from({ length }, (_, index) => { const date = new Date(start); date.setDate(start.getDate() + index);
      return { date, value: this.toDateValue(date), weekday: new Intl.DateTimeFormat(this.locale, { weekday: 'short' }).format(date),
        dayLabel: new Intl.DateTimeFormat(this.locale, { day: '2-digit', month: 'short' }).format(date), isToday: this.toDateValue(date) === today }; });
  }

  private defaultFilters(): { companiesRecordId: string; orgUnitId: string; employeeId: string; status: string; weekDate: string; dateTo: string } {
    const today = new Date(); const monday = new Date(today);
    monday.setHours(0, 0, 0, 0); monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
    const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6);
    return { companiesRecordId: '', orgUnitId: '', employeeId: '', status: '', weekDate: this.toDateValue(monday), dateTo: this.toDateValue(sunday) };
  }
  private syncRangeDates(): void {
    this.rangeStartDate = this.parseDate(this.filters.weekDate);
    this.rangeEndDate = this.parseDate(this.filters.dateTo);
    this.syncRangePickerValue();
  }

  private syncRangePickerValue(): void {
    this.rangePickerValue = this.rangeStartDate
      ? [this.rangeStartDate, ...(this.rangeEndDate ? [this.rangeEndDate] : [])]
      : null;
  }

  private normalizeResult(item: AttendanceResultRecord): AttendanceResultRecord {
    return { ...item, status: item.status || 'pending', employeeName: item.employeeName || item.employeeNumber || '—',
      eventIds: Array.isArray(item.eventIds) ? item.eventIds : [], incidentCodes: Array.isArray(item.incidentCodes) ? item.incidentCodes : [],
      flags: { hasDelay: item.flags?.hasDelay === true, hasEarlyExit: item.flags?.hasEarlyExit === true,
        hasOvertime: item.flags?.hasOvertime === true, workedOnHoliday: item.flags?.workedOnHoliday === true,
        hasMissingPunch: item.flags?.hasMissingPunch === true, hasIncident: item.flags?.hasIncident === true } };
  }
  private earlyExitMinutes(result: AttendanceResultRecord): number {
    const metrics = result.rawPayload?.metrics as Record<string, unknown> | null | undefined;
    return [result.earlyExitMinutes, metrics?.['earlyExitMinutes'], metrics?.['early_exit_minutes']]
      .map(Number).find((value) => Number.isFinite(value) && value > 0) || 0;
  }

  private async loadCompanies(): Promise<void> {
    try { const response = await firstValueFrom(this.apiService.get<CompaniesRecord[] | CollectionResponse<CompaniesRecord>>('companies-records', { params: { isActive: 'true' } }));
      this.companies = this.extractCollection(response, 'companiesRecords').filter((item) => item.isActive !== false).sort((a, b) => a.name.localeCompare(b.name));
      this.filters.companiesRecordId = this.companySelection.resolveSharedSelection(this.companies, this.filters.companiesRecordId); }
    catch { this.companies = []; }
  }
  private async loadOrgUnits(): Promise<void> {
    try { const response = await firstValueFrom(this.apiService.get<OrgUnitRecord[] | CollectionResponse<OrgUnitRecord>>('org-units'));
      this.orgUnits = this.extractCollection(response, 'orgUnits').filter((item) => item.isActive !== false && (!this.filters.companiesRecordId || item.companiesRecordId === this.filters.companiesRecordId)).sort((a, b) => a.name.localeCompare(b.name)); }
    catch { this.orgUnits = []; }
  }
  private async loadEmployees(search = ''): Promise<void> {
    try { const selected = this.selectedEmployee; const params = { page: 1, perPage: 30, isActive: 'true', ...(search ? { search } : {}),
        ...(this.filters.companiesRecordId ? { companiesRecordId: this.filters.companiesRecordId } : {}), ...(this.filters.orgUnitId ? { orgUnitId: this.filters.orgUnitId } : {}) };
      const response = await firstValueFrom(this.apiService.get<EmployeeRecord[] | CollectionResponse<EmployeeRecord>>('employees', { params }));
      const items = this.extractCollection(response, 'employees').filter((item) => item.isActive !== false).sort((a, b) => a.fullName.localeCompare(b.fullName));
      this.employees = selected && !items.some((item) => item.employeeId === selected.employeeId) ? [selected, ...items] : items; }
    catch { this.employees = []; }
  }
  private extractCollection<T>(response: T[] | CollectionResponse<T>, key: 'companiesRecords' | 'orgUnits' | 'employees'): T[] {
    if (Array.isArray(response)) return response; return response[key] || response.data || response.items || response.results || [];
  }
  private employeeLabel(employee: EmployeeRecord): string { return `${employee.fullName || employee.employeeNumber} · ${employee.employeeNumber}`; }
  private formatMinutes(value?: number | null): string {
    const totalMinutes = Math.max(0, Math.round(Number(value) || 0));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours} h ${String(minutes).padStart(2, '0')} min`;
  }
  private formatTime(value?: string | null, timezone?: string): string {
    if (!value) return '—'; const date = new Date(value); if (Number.isNaN(date.getTime())) return '—';
    return new Intl.DateTimeFormat(this.locale, { hour: '2-digit', minute: '2-digit', hour12: this.locale !== 'en-US', ...(timezone ? { timeZone: timezone } : {}) }).format(date);
  }
  private countRows(filter: Exclude<ResultViewFilter, 'all' | 'incidents'>): number {
    return this.rows.filter((row) => this.rowCategory(row) === filter).length;
  }
  private rowCategory(row: ResultRow): Exclude<ResultViewFilter, 'all' | 'incidents'> {
    const results = [...row.cells.values()];
    if (results.some((result) => result.status === 'incomplete')) return 'incomplete';
    if (results.some((result) => result.flags?.hasDelay || result.flags?.hasEarlyExit)) return 'delay';
    if (results.some((result) => result.status === 'absent')) return 'absent';
    return 'complete';
  }
  private hasResultIncident(result: AttendanceResultRecord): boolean {
    return result.flags?.hasIncident === true || result.flags?.hasDelay === true || result.flags?.hasEarlyExit === true ||
      result.status === 'incomplete' || result.status === 'absent' || (result.incidentCodes?.length || 0) > 0;
  }
  private quickLaborContextValue(paths: string[]): string {
    const context = this.selectedQuickResult?.rawPayload?.laborContext;
    if (!context || typeof context !== 'object') return '';
    for (const path of paths) {
      let value: unknown = context;
      for (const segment of path.split('.')) {
        if (!value || typeof value !== 'object') { value = undefined; break; }
        value = (value as Record<string, unknown>)[segment];
      }
      if (typeof value === 'string' && value.trim()) return value.trim();
    }
    return '';
  }
  private formatDateLong(value: string): string {
    const date = this.parseDate(value);
    if (!date) return value || '—';
    const formatted = new Intl.DateTimeFormat(this.locale, { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' }).format(date);
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  }
  private get locale(): string { return this.translate.currentLang?.startsWith('en') ? 'en-US' : 'es-MX'; }
  private parseDate(value: string): Date | null { const [year, month, day] = value.split('-').map(Number); return year && month && day ? new Date(year, month - 1, day) : null; }
  private toDateValue(date: Date): string { return `${date.getFullYear()}-${`${date.getMonth() + 1}`.padStart(2, '0')}-${`${date.getDate()}`.padStart(2, '0')}`; }
  private notify(panelClass: string, translationKey: string): void {
    this.snackBar.open(this.t(translationKey), undefined, { duration: 3500, panelClass: [panelClass], horizontalPosition: 'center', verticalPosition: 'bottom' });
  }
  private t(key: string, params?: Record<string, unknown>): string { return this.translate.instant(key, params); }
}
